import { Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  Companies,
  Delivery,
  Order,
  Personen,
  Staff,
  WebserviceResponse
} from "../../../_shared/interfaces";
import { ClarityModule, ClrInputModule, ClrSelectModule, ClrStepperModule } from "@clr/angular";
import { RequestCompanySearchComponent } from "../../requests/request-company-search/request-company-search.component";
import { DatePipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import {Router, RouterLink} from "@angular/router";
import {
  AlertService,
  IAlertType,
  PersonsStoreService,
  StaffStoreService,
  TransportStoreService
} from "../../../_shared/services";

import { environment as env } from "../../../../environments/environment";
import {TranslocoDirective} from "@jsverse/transloco";

import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';
import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-order-transport-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    ClrStepperModule,
    ReactiveFormsModule,
    RequestCompanySearchComponent,
    ClrInputModule,
    ClrSelectModule,
    NgIf,
    ClarityModule,
    DatePipe,
    RouterLink,
    NgForOf,
    JsonPipe,
    TranslocoDirective
  ],
  templateUrl: './order-transport-form.component.html',
  styleUrl: './order-transport-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})
export class OrderTransportFormComponent implements OnInit, OnChanges {
  transportForm!: FormGroup;
  private defaultDate: Date = new Date();
  companyId: string = '-';
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.transportForm);
  }

  @Input() projectId: string = '';
  @Input() jobId: number = 0;
  @Input() orderId: number = 0;
  @Input() transportId: number = 0;
  @Input() transportData?: Delivery;
  @Input() isUpdateMode = true;
  @Output() formSubmit = new EventEmitter<Order>();

  devMode: boolean = false;

  constructor(
    private persss: PersonsStoreService,
    private transss: TransportStoreService,
    private staffService: StaffStoreService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private router: Router,
    private alertDispatcher: AlertService
  ) {
    this.devMode = env.stages.dev;
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['transportData'] && this.transportData) {
      this.patchFormValues(this.transportData);
      if (this.transportData.company_id) {
        this.loadCustomerList(this.transportData.company_id);
      }
    }
    this.companyId = this.splitProjectNumber(this.projectId || '---', 0);
  }

  ngOnInit(): void {
    this.loadStaff();
    this.loadCustomerList(parseInt(this.companyId));
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  private initForm() {
    this.transportForm = this.fb.group({
      stepI: this.fb.group({
        project_id: this.projectId,
        transport_id: this.transportId,
        company_name: [''],
        street: [''],
        postcode: [''],
        city: [''],
        country: [''],
        status: [''],
        name_project: new FormControl('', [Validators.required]),
        description: [''],
        request_id: 0,
        order_id:  0,
        delivery_date: new FormControl(this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy')),
        budget: 0
      }),
      stepII: this.fb.group({
        employee_phm: 0,
      }),
      stepIII: this.fb.group({
        employee_leader: 0,
      })
    });
  }

  private patchFormValues(data: Delivery) {
    if (!data) { return; }

    const formValue = {
      stepI: {
        project_id: this.projectId || '',
        transport_id: this.transportId || 0,
        company_name: data.company_name || '',
        street: data.street || '',
        postcode: data.postcode || '',
        city: data.city || '',
        country: this.mapCountryForForm(data.country || ''),
        status: data.status || '',
        name_project: data.name_project || '',
        description: data.description || '',
        request_id: data.request_id || 0,
        order_id: data.order_id || 0,
        delivery_date: this.datePipe.transform( data.delivery_date, 'dd.MM.yyyy') || '',
        budget:  data.budget || 0,
      },
      stepII: {
        employee_phm: data.employee_phm || 0,
      },
      stepIII:{
        employee_leader: data.employee_leader || 0,
      }
    };
    this.transportForm.patchValue(formValue);
  }

  preFillRow(supplier: Companies) {
    const update = {
      stepI:
        {
          company_name: supplier['company'],
          street: supplier['street'],
          postcode: supplier['postcode'],
          city: supplier['city'],
          country: this.mapCountryForForm(supplier['country']),
        }
    }
    this.transportForm.patchValue(update);
    if (supplier.cid) {
      this.loadCustomerList(supplier.cid);
    }
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  private normalizeCountryForBackend(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  projectStaff(): number {
    return this.transportForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  loadCustomerList(companyid: number) {
    this.persss.getPersonInCompany(companyid, true)
      .subscribe({
        next: data => {
          if (!data || !data.object || data.object.length === 0) {
            this.alertDispatcher.show({
              message: ' Keine Person gefunden! ',
              clrAlertType: IAlertType.WARNING
            }, 5000);
            return;
          }
          this.persons = data.object;
        },
        complete: () => {
          // Sortiere die Liste nach dem Nachnamen
          this.persons.sort((a, b) => {
            if (a.lastname < b.lastname) {
              return -1;
            }
            if (a.lastname > b.lastname) {
              return 1;
            }
            return 0;
          });

        },
        error: () => {
          return
        }
      });
  }

  updateTransport() {
    if (this.transportForm.valid) {
      const formValue = this.transportForm.value;
      const payload = {
        ...formValue,
        stepI: {
          ...formValue.stepI,
          country: this.normalizeCountryForBackend(formValue?.stepI?.country),
        }
      };
      this.transss.updateTransport(payload).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Transportschein erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(payload);
          this.router.navigate([`transport/details/${this.projectId}/${this.jobId}/${this.transportId}/${this.orderId}`]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren des Transportscheins: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }

}
